// How the cart summary should present the applied coupon.
//
// Kept as a pure function (no React) so the rule is testable and lives in one
// place instead of being re-derived inline for each summary line.
//
// A plain FREE_SHIPPING coupon takes nothing off the item prices, so
// Cart#coupon_value (= subtotal-before-coupon minus subtotal) is $0 for it and
// the Discount line read "-$0.00" above a "Subtotal after Savings" identical to
// the Subtotal. What the customer actually saves is the shipping fee, so that
// one type gets its own presentation: the Discount line shows the shipping fee
// (from the cart's price list), the redundant subtotal-after-savings line is
// dropped, and the Shipping line strikes out the fee it waived.
//
// FREE_SHIPPING_OVER is shipping-only too, but waives the fee only once the
// cart's subtotal reaches the coupon's minimum. Whether it does is the
// SERVER's call (`cart.waivesShipping`, the same predicate the charge uses).
// Under the minimum the Discount line names the threshold ("Free shipping at
// $50") and the Shipping line charges the fee; how far the cart is from it is
// the progress bar's job (`freeShippingProgress`, drawn by
// FreeShippingProgress at the top of the summary for both threshold types).
//
// The *_WITH_FREE_SHIPPING combos DO discount the item prices, so they keep the
// normal discount lines and share only the shipping-line treatment. Every other
// coupon type is unaffected.
const SHIPPING_ONLY_TYPES = ['FREE_SHIPPING', 'FREE_SHIPPING_OVER'];
const SHIPPING_THRESHOLD_TYPES = ['FREE_SHIPPING_OVER', 'PERCENTAGE_WITH_FREE_SHIPPING_OVER'];

export const couponSummary = (cart) => {
  const coupon = cart?.coupon;
  const couponType = coupon?.couponType || '';
  const shippingOnly = SHIPPING_ONLY_TYPES.includes(couponType);
  const freeShipping = couponType.includes('FREE_SHIPPING');
  // A payload without the flag (an older server) waives whatever the type
  // says; a payload with it is believed.
  const waivesShipping = freeShipping && cart?.waivesShipping !== false;
  // The fee being waived. Cart#shipping ignores the coupon (Cart#api_total is
  // what drops it from the charge), so this is the real pre-coupon fee.
  const waivedShipping = Number(cart?.shipping) || 0;

  // The progress bar's numbers, for a threshold offer: judged on the subtotal
  // BEFORE the coupon and before shipping, the same number checkout judges.
  // Null for every other coupon, and when the offer carries no minimum.
  const minimum = Number(coupon?.freeShippingMinimum);
  let freeShippingProgress = null;
  if (SHIPPING_THRESHOLD_TYPES.includes(couponType) && minimum > 0) {
    const subtotal = Number(cart?.subtotalWithoutCoupon) || 0;
    const remaining = Number(cart?.amountToFreeShipping) || 0;
    freeShippingProgress = {
      minimum, subtotal, remaining, reached: waivesShipping,
    };
  }

  // A number is currency-formatted with a leading minus by SummaryItem; the
  // label is for the carts that owe no shipping in the first place (digital-only,
  // appointment, $0 subtotal), where "-$0.00" is exactly what we're avoiding.
  let discountValue = Number(cart?.couponValue) || 0;
  if (shippingOnly && waivesShipping) discountValue = waivedShipping > 0 ? waivedShipping : 'Free Shipping';
  if (shippingOnly && !waivesShipping) {
    const minimumLabel = minimum % 1 === 0 ? minimum : minimum.toFixed(2);
    discountValue = minimum > 0 ? `Free shipping at $${minimumLabel}` : 'Free Shipping';
  }

  // A shipping-only threshold coupon under its minimum shows NO Discount line
  // (nor its remove button): the progress bar is the coupon's whole story
  // until it is earned (Katie, 2026-10-21). The percentage combo keeps its
  // line -- that one has money off to show.
  const hideCouponLine = shippingOnly && !waivesShipping && freeShippingProgress !== null;

  return {
    shippingOnly,
    // The OFFER waives shipping, alone or alongside a price discount.
    freeShipping,
    // ... and THIS cart gets it.
    waivesShipping,
    waivedShipping,
    freeShippingProgress,
    hideCouponLine,
    discountValue,
  };
};

export default couponSummary;
